import React, { useState } from 'react';
import {
  Search,
  ChevronDown,
  ArrowLeft,
  X,
  Plus,
  Trash2,
  Edit2,
  FileSpreadsheet,
  FileText,
  CreditCard,
} from 'lucide-react';
import { ResidentSubMenuKey } from '../ResidentDirectory';

interface Props {
  onBack: () => void;
  onNavigate?: (tab: ResidentSubMenuKey) => void;
}

export interface RoomKeycardRecord {
  id: string;
  roomNumber: string;
  cardNumbers: string[];
}

// Exactly matching images 2 & 3 (image (8).png & image (9).png)
const INITIAL_ROOM_KEYCARDS: RoomKeycardRecord[] = [
  {
    id: 'rk-1',
    roomNumber: '1086/01',
    cardNumbers: [
      '12111',
      '123456789',
      '9999999',
      'ED364578',
      '445566',
      '1234567',
      '1234456',
      '098998',
    ],
  },
  {
    id: 'rk-2',
    roomNumber: '1086/02',
    cardNumbers: ['123', '1234567', 'ED01088596334A'],
  },
  {
    id: 'rk-3',
    roomNumber: '1086/03',
    cardNumbers: ['1111111', '0388'],
  },
  {
    id: 'rk-4',
    roomNumber: '1086/04',
    cardNumbers: ['1234788989', '1', 'ED01088596334A'],
  },
  {
    id: 'rk-5',
    roomNumber: '1086/06',
    cardNumbers: ['ED364579', 'ED364578'],
  },
  {
    id: 'rk-6',
    roomNumber: '1086/09',
    cardNumbers: ['003', '001'],
  },
  {
    id: 'rk-7',
    roomNumber: '1086/10',
    cardNumbers: ['23445555', '955955425'],
  },
  {
    id: 'rk-8',
    roomNumber: '1086/20',
    cardNumbers: ['leda5'],
  },
  {
    id: 'rk-9',
    roomNumber: '301/002',
    cardNumbers: [],
  },
  {
    id: 'rk-10',
    roomNumber: '1806/01',
    cardNumbers: ['1233'],
  },
  // Additional records to total 18 records as shown in image 8 & 9
  {
    id: 'rk-11',
    roomNumber: '1086/05',
    cardNumbers: ['555112', '889921'],
  },
  {
    id: 'rk-12',
    roomNumber: '1086/07',
    cardNumbers: ['991200'],
  },
  {
    id: 'rk-13',
    roomNumber: '1086/08',
    cardNumbers: ['CARD-8812'],
  },
  {
    id: 'rk-14',
    roomNumber: '1086/11',
    cardNumbers: ['A108611'],
  },
  {
    id: 'rk-15',
    roomNumber: '1086/12',
    cardNumbers: ['B099231', 'B099232'],
  },
  {
    id: 'rk-16',
    roomNumber: '1086/14',
    cardNumbers: ['141400'],
  },
  {
    id: 'rk-17',
    roomNumber: '1086/15',
    cardNumbers: ['151500', '151501'],
  },
  {
    id: 'rk-18',
    roomNumber: '1086/16',
    cardNumbers: ['161601'],
  },
];

export const ResidentKeycardsView: React.FC<Props> = ({ onBack, onNavigate }) => {
  const [data, setData] = useState<RoomKeycardRecord[]>(INITIAL_ROOM_KEYCARDS);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [showPrintDropdown, setShowPrintDropdown] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Edit / Add modal
  const [selectedRecord, setSelectedRecord] = useState<RoomKeycardRecord | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formRoom, setFormRoom] = useState('');
  const [formCards, setFormCards] = useState('');

  const tabs: { key: ResidentSubMenuKey; label: string }[] = [
    { key: 'members', label: 'เจ้าของร่วม/สมาชิก' },
    { key: 'vehicles', label: 'ยานพาหนะ' },
    { key: 'keycards', label: 'คีย์การ์ด' },
    { key: 'foreign_ownership', label: 'กรรมสิทธิ์ชาวต่างชาติ' },
    { key: 'pets', label: 'สัตว์เลี้ยง' },
    { key: 'title_deeds', label: 'กรรมสิทธิ์' },
    { key: 'history_logs', label: 'ประวัติผู้อยู่อาศัย' },
  ];

  // Filtering
  const filteredData = data.filter((r) => {
    const q = appliedSearch.toLowerCase().trim();
    if (!q) return true;
    const matchRoom = r.roomNumber.toLowerCase().includes(q);
    const matchCards = r.cardNumbers.some((c) => c.toLowerCase().includes(q));
    return matchRoom || matchCards;
  });

  // Pagination
  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (validCurrentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchQuery);
    setCurrentPage(1);
  };

  const handleExportPDF = () => {
    setShowPrintDropdown(false);
    window.print();
  };

  const handleExportExcel = () => {
    setShowPrintDropdown(false);
    let csv = 'ห้อง,เลขคีย์การ์ด\n';
    data.forEach((r) => {
      csv += `"${r.roomNumber}","${r.cardNumbers.join('  ')}"\n`;
    });

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ข้อมูลคีย์การ์ด_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRecord) return;
    const cards = formCards
      .split(/\s+/)
      .map((c) => c.trim())
      .filter(Boolean);

    setData(
      data.map((r) =>
        r.id === selectedRecord.id
          ? { ...r, roomNumber: formRoom.trim() || r.roomNumber, cardNumbers: cards }
          : r
      )
    );
    setSelectedRecord(null);
  };

  const handleAddNewRoom = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRoom.trim()) return;

    const cards = formCards
      .split(/\s+/)
      .map((c) => c.trim())
      .filter(Boolean);

    const newRecord: RoomKeycardRecord = {
      id: `rk-${Date.now()}`,
      roomNumber: formRoom.trim(),
      cardNumbers: cards,
    };

    setData([...data, newRecord]);
    setShowAddModal(false);
    setFormRoom('');
    setFormCards('');
  };

  return (
    <div className="font-prompt bg-white min-h-[85vh] rounded-2xl shadow-xs border border-slate-200/80 overflow-hidden">
      {/* 1. Top Sub-Navigation Tabs matching image */}
      <div className="border-b border-slate-200 bg-slate-50/50 px-4 sm:px-6 pt-3 overflow-x-auto">
        <div className="flex items-center gap-6 sm:gap-8 min-w-max text-xs sm:text-sm">
          {tabs.map((tab) => {
            const isActive = tab.key === 'keycards';
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  if (tab.key === 'keycards') return;
                  if (onNavigate) {
                    onNavigate(tab.key);
                  } else {
                    onBack();
                  }
                }}
                className={`pb-3 font-medium transition-colors cursor-pointer relative ${
                  isActive
                    ? 'text-slate-900 font-bold border-b-2 border-teal-600'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        {/* 2. Top Header with Back button and พิมพ์ dropdown matching Image 2 & 3 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="w-7 h-7 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors cursor-pointer shrink-0"
              title="ย้อนกลับ"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-slate-800 tracking-tight">
              ข้อมูลคีย์การ์ด
            </h1>
          </div>

          {/* พิมพ์ Button with Dropdown (PDF / Excel) matching Image 2 & 3 */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowPrintDropdown(!showPrintDropdown)}
              className="px-4 py-1.5 bg-white border border-[#2b72c9] text-[#2b72c9] hover:bg-blue-50/50 rounded-md text-xs sm:text-sm font-medium flex items-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
            >
              <span>พิมพ์</span>
              <ChevronDown className="w-3.5 h-3.5 text-[#2b72c9]" />
            </button>

            {showPrintDropdown && (
              <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg border border-slate-200 py-1 z-30 animate-in fade-in">
                <button
                  type="button"
                  onClick={handleExportPDF}
                  className="w-full px-4 py-2 text-left text-xs sm:text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2 cursor-pointer"
                >
                  <FileText className="w-4 h-4 text-slate-500" /> PDF
                </button>
                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="w-full px-4 py-2 text-left text-xs sm:text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2 cursor-pointer"
                >
                  <FileSpreadsheet className="w-4 h-4 text-slate-500" /> Excel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 3. Search Bar matching image 2 & 3: Input + Blue "ค้นหา" button */}
        <div className="flex justify-end pt-1">
          <form onSubmit={handleSearchSubmit} className="flex items-center">
            <div className="relative">
              <input
                type="text"
                placeholder="ค้นหา"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48 sm:w-64 pl-8 pr-3 py-1.5 bg-white border border-slate-300 rounded-l-md text-xs sm:text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-[#0056b3] focus:border-[#0056b3]"
              />
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <button
              type="submit"
              className="px-4 py-1.5 bg-[#0056b3] hover:bg-[#004494] text-white rounded-r-md text-xs sm:text-sm font-medium transition-colors cursor-pointer border border-[#0056b3]"
            >
              ค้นหา
            </button>
          </form>
        </div>

        {/* 4. Keycard Table matching image 2 & 3 */}
        <div className="border border-slate-200 rounded-md overflow-hidden bg-white shadow-2xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm text-slate-700">
              <thead className="bg-[#f1f3f5] border-b border-slate-200 text-xs font-semibold text-slate-800">
                <tr>
                  <th className="py-2.5 px-4 w-36 sm:w-44 font-semibold text-slate-800">ห้อง</th>
                  <th className="py-2.5 px-4 font-semibold text-slate-800">เลขคีย์การ์ด</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="py-10 text-center text-slate-400 text-xs">
                      ไม่พบข้อมูลคีย์การ์ดสำหรับห้องที่ค้นหา
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((r, index) => (
                    <tr
                      key={r.id}
                      onClick={() => {
                        setSelectedRecord(r);
                        setFormRoom(r.roomNumber);
                        setFormCards(r.cardNumbers.join(' '));
                      }}
                      className="hover:bg-slate-50 transition-colors cursor-pointer group"
                    >
                      {/* ห้อง (Blue or dark text as in image) */}
                      <td className="py-3 px-4 font-normal text-[#2b72c9] whitespace-nowrap align-top">
                        {r.roomNumber}
                      </td>

                      {/* เลขคีย์การ์ด (Separated with spaces matching screenshot) */}
                      <td className="py-3 px-4 text-slate-800 font-normal align-top leading-relaxed tracking-wide">
                        {r.cardNumbers.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                            {r.cardNumbers.map((c, i) => (
                              <span key={i} className="inline-block">
                                {c}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 5. Pagination Footer matching image 2 & 3 */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm text-slate-600 pt-2 px-1">
          {/* Left: แสดง 10 รายการ/หน้า */}
          <div className="flex items-center gap-2">
            <span>แสดง</span>
            <div className="relative inline-block">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="appearance-none bg-white border border-slate-300 rounded px-2.5 py-1 pr-6 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#0056b3] cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <span>รายการ/หน้า</span>
          </div>

          {/* Right: รายการทั้งหมด 18  [1] [2] */}
          <div className="flex items-center gap-3">
            <span>รายการทั้งหมด {totalItems}</span>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-7 h-7 rounded border text-xs flex items-center justify-center transition-colors cursor-pointer ${
                    currentPage === pageNum
                      ? 'border-[#0056b3] bg-[#0056b3] text-white font-semibold'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Add New Room Keycard Record Button */}
        <div className="pt-2 flex justify-start">
          <button
            type="button"
            onClick={() => {
              setFormRoom('');
              setFormCards('');
              setShowAddModal(true);
            }}
            className="text-xs text-teal-700 hover:text-teal-800 font-medium flex items-center gap-1 cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" /> เพิ่มข้อมูลคีย์การ์ดห้องใหม่
          </button>
        </div>
      </div>

      {/* Modal: Edit Room Keycards */}
      {selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95">
            <div className="bg-[#0056b3] p-4 text-white flex items-center justify-between">
              <h3 className="font-bold text-sm sm:text-base flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> จัดการคีย์การ์ดห้อง {selectedRecord.roomNumber}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="text-white/80 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="p-5 space-y-4 text-xs sm:text-sm">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  หมายเลขห้อง
                </label>
                <input
                  type="text"
                  value={formRoom}
                  onChange={(e) => setFormRoom(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0056b3]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  หมายเลขคีย์การ์ด{' '}
                  <span className="text-slate-400 font-normal">(คั่นด้วยการเว้นวรรค)</span>
                </label>
                <textarea
                  rows={4}
                  value={formCards}
                  onChange={(e) => setFormCards(e.target.value)}
                  placeholder="เช่น 12111 123456789 ED364578 445566"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0056b3]"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setData(data.filter((r) => r.id !== selectedRecord.id));
                    setSelectedRecord(null);
                  }}
                  className="text-rose-600 hover:text-rose-700 text-xs font-medium flex items-center gap-1 cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" /> ลบรายการห้องนี้
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedRecord(null)}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs cursor-pointer"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-1.5 bg-[#0056b3] hover:bg-[#004494] text-white rounded-md text-xs font-medium cursor-pointer shadow-2xs"
                  >
                    บันทึกการแก้ไข
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Add New Room Keycards */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95">
            <div className="bg-[#0056b3] p-4 text-white flex items-center justify-between">
              <h3 className="font-bold text-sm sm:text-base flex items-center gap-2">
                <Plus className="w-4 h-4" /> เพิ่มข้อมูลคีย์การ์ดห้องใหม่
              </h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-white/80 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddNewRoom} className="p-5 space-y-4 text-xs sm:text-sm">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  หมายเลขห้อง *
                </label>
                <input
                  type="text"
                  required
                  placeholder="เช่น 1086/25"
                  value={formRoom}
                  onChange={(e) => setFormRoom(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0056b3]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  หมายเลขคีย์การ์ด{' '}
                  <span className="text-slate-400 font-normal">(คั่นด้วยการเว้นวรรค)</span>
                </label>
                <textarea
                  rows={3}
                  value={formCards}
                  onChange={(e) => setFormCards(e.target.value)}
                  placeholder="เช่น 12111 123456789 ED364578"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0056b3]"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs cursor-pointer"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-[#0056b3] hover:bg-[#004494] text-white rounded-md text-xs font-medium cursor-pointer shadow-2xs"
                >
                  บันทึกข้อมูล
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
